import { Component } from '@theme/component';
import { isClickedOutside } from '@theme/utilities';

/**
 * Tendina nuance con ciuffi.
 *
 * Interfaccia (bottone + pannello) sopra un <select> nativo che resta il MOTORE:
 * qui gestiamo solo apri/chiudi del pannello. La selezione di una riga (che
 * imposta il valore del select e lancia `change`) arriverà nel passo 3.
 *
 * Modellato su DropdownLocalizationComponent (assets/localization.js).
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} button - Il bottone che apre/chiude.
 * @property {HTMLDivElement} panel - Il pannello con le righe.
 * @property {HTMLSelectElement} [select] - Il select nativo (motore + fallback).
 *
 * @extends {Component<Refs>}
 */
class CustomVariantDropdownComponent extends Component {
  // Il nascondimento del select nativo e la comparsa dell'interfaccia nuova sono
  // gestiti da CSS statico agganciato a <html> (html:not(.no-js) / html.no-js):
  // regge anche quando morph rigenera il picker. Qui niente stato a runtime.

  get isHidden() {
    return this.refs.panel.hasAttribute('hidden');
  }

  /**
   * Apre o chiude il pannello.
   */
  toggleSelector() {
    return this.isHidden ? this.showPanel() : this.hidePanel();
  }

  /**
   * Click su una riga: imposta il valore del select nascosto, lancia `change`
   * (la catena esistente aggiorna prezzo/immagine/disponibilità da sola),
   * aggiorna il testo del bottone e chiude. NIENT'ALTRO.
   *
   * @param {Event} event
   */
  selectValue(event) {
    const option = event.target instanceof Element ? event.target.closest('.custom-dropdown__option') : null;
    if (!option) return;

    const value = option.dataset.value;
    const select = this.refs.select;
    if (!select || value == null) return;

    // 1) imposta il valore del select + 2) lancia change (in bubbling → catena)
    select.value = value;
    select.dispatchEvent(new Event('change', { bubbles: true }));

    // 4) aggiorna il testo del bottone con la nuance scelta
    if (this.refs.buttonText) this.refs.buttonText.textContent = value;

    // ...e il ciuffo del bottone (o lo nasconde se la nuance non ne ha)
    const img = this.refs.buttonImage;
    if (img) {
      const ciuffo = option.dataset.ciuffo;
      if (ciuffo) {
        img.setAttribute('src', ciuffo);
        img.hidden = false;
      } else {
        img.removeAttribute('src');
        img.hidden = true;
      }
    }

    // memorizza la riga scelta (resterà evidenziata alla riapertura)
    this.#markSelected(option);

    // 3) chiude il pannello
    this.hidePanel();
  }

  /**
   * Segna una sola riga come selezionata.
   *
   * @param {Element} selected
   */
  #markSelected(selected) {
    for (const el of this.querySelectorAll('.custom-dropdown__option')) {
      const isSelected = el === selected;
      el.classList.toggle('is-selected', isSelected);
      el.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    }
  }

  /**
   * Apre il pannello.
   */
  showPanel() {
    if (!this.isHidden) return;

    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener('click', this.#handleClickOutside);

    this.refs.panel.removeAttribute('hidden');
    this.refs.button.setAttribute('aria-expanded', 'true');
  }

  /**
   * Chiude il pannello.
   */
  hidePanel = () => {
    if (this.isHidden) return;

    this.removeEventListener('keyup', this.#handleKeyUp);
    document.removeEventListener('click', this.#handleClickOutside);

    this.refs.button?.setAttribute('aria-expanded', 'false');
    this.refs.panel.setAttribute('hidden', '');
  };

  /**
   * Chiude se si clicca fuori dal componente.
   *
   * @param {PointerEvent} event
   */
  #handleClickOutside = (event) => {
    if (isClickedOutside(event, this)) {
      this.hidePanel();
    }
  };

  /**
   * Escape chiude e riporta il fuoco sul bottone.
   *
   * @param {KeyboardEvent} event
   */
  #handleKeyUp = (event) => {
    if (event.key === 'Escape') {
      this.hidePanel();
      event.stopPropagation();
      this.refs.button?.focus();
    }
  };
}

if (!customElements.get('custom-variant-dropdown-component')) {
  customElements.define('custom-variant-dropdown-component', CustomVariantDropdownComponent);
}
